/**
 * S12 · Capture Detail — read-only viewer for a single capture row.
 * Photo captures render the blob straight from OPFS via a cached object
 * URL; other kinds show their structured payload.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { Capture } from '@kapture/types';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  PhotoPlaceholder,
  ScreenFrame,
  StatusBar,
  T,
  Waveform,
} from '@/components/primitives';
import { useCapture, useUpdateCaptureSubjectMutation } from '@kapture/client-core';
import { useState } from 'react';
import { useBlobUrl } from '@/lib/device/useBlobUrl';

export const Route = createFileRoute('/sessions/$sessionId/captures/$captureId')({
  component: CaptureDetailPage,
  staticData: { mobile: true },
});

function CaptureDetailPage() {
  const { sessionId, captureId } = Route.useParams();
  const { data: capture, isLoading } = useCapture(captureId);

  if (isLoading) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <Centered>Loading capture…</Centered>
      </ScreenFrame>
    );
  }

  if (!capture) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <Header sessionId={sessionId} title="Capture" subtitle="Not found" />
        <Centered>This capture doesn't exist or was removed.</Centered>
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <Header
        sessionId={sessionId}
        title={titleFor(capture)}
        subtitle={new Date(capture.createdAt).toLocaleString()}
      />
      <div style={{ flex: 1, overflow: 'auto' }}>
        <MediaBlock capture={capture} />
        <MetadataBlock capture={capture} />
      </div>
      <NextCaptureBar capture={capture} sessionId={sessionId} />
      <GestureBar />
    </ScreenFrame>
  );
}

function Header({
  sessionId,
  title,
  subtitle,
}: {
  sessionId: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div
      style={{
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderBottom: `1px solid ${T.stroke}`,
      }}
    >
      <Link
        to="/sessions/$sessionId"
        params={{ sessionId }}
        aria-label="Back"
        style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
      >
        <Ico d={I.back} size={20} />
      </Link>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            fontFamily: T.font,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginTop: 1,
          }}
        >
          {subtitle}
        </div>
      </div>
      <Ico d={I.moreV} size={18} />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        color: T.textDim,
        fontFamily: T.font,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

function titleFor(c: Capture): string {
  switch (c.kind) {
    case 'note':
      return c.title || 'Untitled note';
    case 'voice':
      return 'Voice note';
    case 'photo':
      return 'Photo';
    case 'gps':
      return 'Location pin';
    case 'barcode':
      return `Asset · ${c.value}`;
    default:
      return 'Capture';
  }
}

function MediaBlock({ capture }: { capture: Capture }) {
  if (capture.kind === 'photo' && capture.blobPath) {
    return <PhotoMedia path={capture.blobPath} />;
  }
  if (capture.kind === 'photo') {
    return (
      <div style={{ background: T.surface2 }}>
        <PhotoPlaceholder h={320} tone="cloudy" />
      </div>
    );
  }
  if (capture.kind === 'video' && capture.blobPath) {
    return <VideoMedia path={capture.blobPath} durationMs={capture.durationMs} />;
  }
  if (capture.kind === 'voice') {
    return (
      <div style={{ padding: 20, background: T.surface2 }}>
        <Waveform bars={40} active={0} color={T.textDim} bg={T.stroke2} h={48} />
        {capture.transcript && (
          <div
            style={{
              fontSize: 13,
              color: T.text,
              fontFamily: T.font,
              marginTop: 12,
              lineHeight: 1.55,
            }}
          >
            “{capture.transcript}”
          </div>
        )}
      </div>
    );
  }
  if (capture.kind === 'note') {
    return (
      <div
        style={{
          padding: 20,
          fontFamily: T.font,
          fontSize: 15,
          color: T.text,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}
      >
        {capture.body || <span style={{ color: T.textMute }}>(empty)</span>}
      </div>
    );
  }
  if (capture.kind === 'barcode') {
    return (
      <div
        style={{
          padding: 24,
          background: T.surface2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {capture.format ?? 'Typed'}
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            fontFamily: T.mono,
            letterSpacing: '-0.01em',
            textAlign: 'center',
            wordBreak: 'break-all',
          }}
        >
          {capture.value}
        </div>
      </div>
    );
  }
  return null;
}

/**
 * The clip, played from the device rather than the server.
 *
 * `useBlobUrl` reads it out of OPFS, which is the only source that works
 * offline — and offline is the state this screen is most often opened in,
 * since the inspector is reviewing what they just recorded. The server copy
 * needs a `Bearer` token, so it cannot go in a `src` at all.
 *
 * No `autoPlay`: the inspector arrived here to look at a list of captures,
 * and a clip narrating itself the moment the screen opens is startling in a
 * quiet building and useless in a loud one.
 */
function VideoMedia({ path, durationMs }: { path: string; durationMs: number }) {
  const url = useBlobUrl(path);
  return (
    <div style={{ background: '#000', minHeight: 240, position: 'relative' }}>
      {url ? (
        <video
          src={url}
          controls
          playsInline
          preload="metadata"
          // `contain`, not `cover`: the reason to record video at all was to
          // catch what a still framed out, so cropping it here throws away
          // the thing the clip was for.
          style={{ width: '100%', display: 'block', maxHeight: 420, objectFit: 'contain' }}
        />
      ) : (
        <div
          style={{
            height: 320,
            display: 'grid',
            placeItems: 'center',
            color: T.textMute,
            fontFamily: T.mono,
            fontSize: 12,
          }}
        >
          Reading from OPFS…
        </div>
      )}
      {durationMs > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            fontFamily: T.mono,
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {Math.round(durationMs / 1000)}s
        </div>
      )}
    </div>
  );
}

function PhotoMedia({ path }: { path: string }) {
  const url = useBlobUrl(path);
  return (
    <div style={{ background: '#000', minHeight: 240 }}>
      {url ? (
        <img
          src={url}
          alt="Captured photo"
          style={{ width: '100%', display: 'block', maxHeight: 420, objectFit: 'contain' }}
        />
      ) : (
        <div
          style={{
            height: 320,
            display: 'grid',
            placeItems: 'center',
            color: T.textMute,
            fontFamily: T.mono,
            fontSize: 12,
          }}
        >
          Reading from OPFS…
        </div>
      )}
    </div>
  );
}

function MetadataBlock({ capture }: { capture: Capture }) {
  const rows: Array<{ k: string; v: string }> = [
    { k: 'Kind', v: capture.kind },
    {
      k: 'Captured',
      v: new Date(capture.createdAt).toLocaleString(),
    },
    {
      k: 'Sync',
      v: capture.blobPath ? `Blob queued · ${formatBytes(capture.byteSize)}` : 'Queued for upload',
    },
  ];
  if (capture.gps) {
    rows.push({
      k: 'GPS',
      v: `${capture.gps.lat.toFixed(5)}, ${capture.gps.lng.toFixed(5)} · ±${Math.round(capture.gps.accuracyMeters ?? 0)}m`,
    });
  }
  if (capture.kind === 'photo' && capture.width && capture.height) {
    rows.push({ k: 'Dimensions', v: `${capture.width}×${capture.height}` });
  }

  return (
    <div
      style={{
        padding: 16,
        borderTop: `1px solid ${T.stroke}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {capture.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {capture.tags.map((t) => (
            <Chip key={t} tone="brandSoft" size="sm">
              {t}
            </Chip>
          ))}
        </div>
      )}
      <SubjectRow capture={capture} />
      <div
        style={{
          border: `1px solid ${T.stroke}`,
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {rows.map((r, i) => (
          <div
            key={r.k}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 12px',
              borderBottom: i < rows.length - 1 ? `1px solid ${T.stroke}` : 'none',
              fontFamily: T.font,
            }}
          >
            <div
              style={{
                width: 92,
                fontSize: 10,
                fontFamily: T.mono,
                color: T.textMute,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {r.k}
            </div>
            <div style={{ flex: 1, fontSize: 13, color: T.text, wordBreak: 'break-word' }}>
              {r.v}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * What this capture is of, and the means to correct it.
 *
 * Sticky subject context is right for the walk — an inspector doing forty
 * doors should not re-declare the door for every photo — but it is
 * occasionally wrong: a shot taken between doors lands on the one just left
 * behind. A mis-filed photo in an audit record is worse than an unfiled one,
 * so this is editable, and clearing it back to site-level is one tap rather
 * than an impossibility.
 */
function SubjectRow({ capture }: { capture: Capture }) {
  const update = useUpdateCaptureSubjectMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(capture.subjectRef ?? '');

  function commit(next: string | null) {
    update.mutate({ captureId: capture.id, subjectRef: next });
    setEditing(false);
  }

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(draft.trim() || null);
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="Code on the thing this is of"
          aria-label="Subject"
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 10px',
            borderRadius: 8,
            border: `1px solid ${T.stroke2}`,
            background: T.surface,
            color: T.text,
            fontFamily: T.mono,
            fontSize: 13,
            outline: 'none',
          }}
        />
        <Btn tone="dark" size="sm" onClick={() => commit(draft.trim() || null)}>
          Save
        </Btn>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 10,
        border: `1px dashed ${T.stroke2}`,
        background: capture.subjectRef ? T.brandSoft : 'transparent',
      }}
    >
      <Ico d={I.tag} size={14} stroke={capture.subjectRef ? T.brandText : T.textMute} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 9,
            fontFamily: T.mono,
            color: T.textMute,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Subject
        </div>
        <div
          style={{
            fontSize: 13,
            fontFamily: capture.subjectRef ? T.mono : T.font,
            fontWeight: 600,
            color: capture.subjectRef ? T.text : T.textMute,
            wordBreak: 'break-all',
          }}
        >
          {capture.subjectRef ?? 'Site level — not of one thing'}
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          setDraft(capture.subjectRef ?? '');
          setEditing(true);
        }}
        style={linkBtn}
      >
        {capture.subjectRef ? 'Change' : 'Set'}
      </button>
      {capture.subjectRef && (
        <button type="button" onClick={() => commit(null)} style={linkBtn}>
          Clear
        </button>
      )}
    </div>
  );
}

const linkBtn = {
  background: 'none',
  border: 'none',
  padding: 4,
  color: T.brandText,
  fontFamily: T.font,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
} as const;

/**
 * Where a capture screen ends, and the way back out of it.
 *
 * Every kind lands here after it is written, and until now the only way on was
 * the back chevron in the header — so a second photograph of the same door cost
 * four taps: back, the dial, the mode, the shutter. On a forty-door round with
 * three shots each that is several hundred taps spent re-declaring what the
 * inspector never stopped doing, and it lands hardest on exactly the walk the
 * subject was built to serve.
 *
 * Both actions are here rather than one, because the two ways out of a capture
 * are "another like that" and "done with this" — and a header chevron is not a
 * button anyone finds with a thumb, in gloves, on a ladder.
 *
 * The primary names the subject when there is one. "Another for D-114" is the
 * sentence the inspector is already thinking; it also makes the sticky context
 * visible at the moment they are about to add to it, which is the moment a
 * forgotten subject would start mis-filing things.
 */
function NextCaptureBar({ capture, sessionId }: { capture: Capture; sessionId: string }) {
  const navigate = useNavigate();
  const subject = capture.subjectRef;
  const route = CAPTURE_ROUTE[capture.kind];

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: '10px 16px',
        borderTop: `1px solid ${T.stroke}`,
        background: T.surface,
      }}
    >
      <Btn
        tone="light"
        size="md"
        onClick={() => navigate({ to: '/sessions/$sessionId', params: { sessionId } })}
      >
        Session
      </Btn>
      {route && (
        <Btn
          tone="primary"
          size="md"
          full
          onClick={() => navigate({ to: route, params: { sessionId } })}
        >
          {subject ? `Another for ${subject}` : `Another ${LABEL_OF[capture.kind]}`}
        </Btn>
      )}
    </div>
  );
}

/**
 * Which screen takes another of this kind.
 *
 * `signature` is absent deliberately: it answers a specific form field rather
 * than being something an inspector takes more of, so "another signature" from
 * here would open a pad with no field to attach it to.
 */
const CAPTURE_ROUTE: Partial<Record<Capture['kind'], string>> = {
  photo: '/sessions/$sessionId/capture/photo',
  video: '/sessions/$sessionId/capture/video',
  voice: '/sessions/$sessionId/capture/voice',
  note: '/sessions/$sessionId/capture/note',
  gps: '/sessions/$sessionId/capture/gps',
  barcode: '/sessions/$sessionId/capture/barcode',
};

const LABEL_OF: Record<Capture['kind'], string> = {
  photo: 'photo',
  video: 'video',
  voice: 'recording',
  note: 'note',
  gps: 'pin',
  barcode: 'scan',
  signature: 'signature',
};
